import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, Trash2, Minimize2, Bug, Lightbulb, Bell, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FloatingChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasShownDailySummary, setHasShownDailySummary] = useState(false);
  const chatEndRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const getUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };

  const user = getUserInfo();

  // Auto-scroll
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Carica notifiche utente
  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/ai/user-notifications`, getAuthHeaders());
      if (response.data.success) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      console.error('Errore caricamento notifiche:', error);
    }
  }, [user]);

  // Carica resoconto giornaliero
  const loadDailySummary = useCallback(async () => {
    if (!user || hasShownDailySummary) return;
    
    // Controlla se già mostrato oggi
    const lastShown = localStorage.getItem('pama_daily_summary_date');
    const today = new Date().toDateString();
    if (lastShown === today) {
      setHasShownDailySummary(true);
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/ai/daily-summary`, getAuthHeaders());
      if (response.data.success && response.data.resoconto) {
        setMessages([{
          role: 'assistant',
          content: response.data.resoconto,
          tipo: 'daily_summary'
        }]);
        setIsOpen(true);
        setHasShownDailySummary(true);
        localStorage.setItem('pama_daily_summary_date', today);
      }
    } catch (error) {
      console.error('Errore caricamento daily summary:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, hasShownDailySummary]);

  // All'avvio: carica notifiche e mostra resoconto giornaliero
  useEffect(() => {
    if (user) {
      loadNotifications();
      // Mostra resoconto giornaliero dopo un breve delay
      const timer = setTimeout(() => {
        loadDailySummary();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user, loadNotifications, loadDailySummary]);

  // Ricarica notifiche ogni 5 minuti
  useEffect(() => {
    const interval = setInterval(loadNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const sendMessage = async (customMessage = null) => {
    const messageToSend = customMessage || inputValue.trim();
    if (!messageToSend || isLoading) return;

    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    
    if (feedbackMode) {
      await submitFeedback(messageToSend);
      return;
    }
    
    setIsLoading(true);

    try {
      const payload = {
        messaggio: messageToSend,
        session_id: sessionId
      };
      
      const response = await axios.post(`${API}/ai/chat`, payload, getAuthHeaders());

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.risposta,
        tipo: response.data.tipo
      }]);
      
    } catch (error) {
      console.error('Errore chat:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Mi dispiace, si è verificato un errore. Riprova tra poco.'
      }]);
      toast.error('Errore nella comunicazione');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setFeedbackMode(null);
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  // Avvia modalità feedback - registra immediatamente
  const startFeedback = (tipo) => {
    setFeedbackMode(tipo);
    const msg = tipo === 'bug'
      ? '🐛 Descrivi il problema e lo registrerò subito.'
      : '💡 Descrivi la tua idea e la registrerò subito.';
    setMessages(prev => [...prev, { role: 'assistant', content: msg }]);
  };

  // Invia feedback direttamente - senza chiedere dettagli
  const submitFeedback = async (descrizione) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/ai/feedback`, {
        tipo: feedbackMode,
        descrizione: descrizione
      }, getAuthHeaders());

      if (response.data.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ Registrato! Riferimento: #${response.data.riferimento}`,
          tipo: 'feedback_salvato'
        }]);
        toast.success('Segnalazione registrata');
      }
    } catch (error) {
      console.error('Errore invio feedback:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Errore nella registrazione. Riprova.'
      }]);
      toast.error('Errore');
    } finally {
      setIsLoading(false);
      setFeedbackMode(null);
    }
  };

  // Richiedi nuovo resoconto
  const refreshDailySummary = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/ai/daily-summary`, getAuthHeaders());
      if (response.data.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.resoconto,
          tipo: 'daily_summary'
        }]);
      }
    } catch (error) {
      toast.error('Errore aggiornamento resoconto');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && !isMinimized && (
        <div 
          className="fixed bottom-44 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-slate-200"
          style={{ animation: 'slideUp 0.3s ease-out' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#005293] to-[#0073CE] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center p-1">
                <img src="/logo.png" alt="PaMa" className="w-full h-full object-contain" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">PaMa Assistant</h3>
                <p className="text-blue-100 text-xs">Assistente AI</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshDailySummary}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                title="Aggiorna resoconto"
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearChat}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                title="Nuova chat"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(true)}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                title="Minimizza"
              >
                <Minimize2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                title="Chiudi"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-gradient-to-br from-[#005293] to-[#0073CE] rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <img src="/logo.png" alt="PaMa" className="w-9 h-9 object-contain" />
                </div>
                <p className="text-slate-600 font-medium mb-1">
                  Ciao{user?.nome ? ` ${user.nome}` : ''}! 👋
                </p>
                <p className="text-slate-400 text-sm mb-3">Come posso aiutarti?</p>
                
                {/* Quick Actions */}
                <div className="space-y-2 mb-3">
                  <div className="flex flex-wrap justify-center gap-2">
                    <button
                      onClick={() => sendMessage('Quali sono le mie attività?')}
                      className="text-xs bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full hover:bg-blue-100 transition-colors text-blue-700"
                    >
                      📋 Attività
                    </button>
                    <button
                      onClick={() => sendMessage('Ho attività in ritardo?')}
                      className="text-xs bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full hover:bg-amber-100 transition-colors text-amber-700"
                    >
                      ⚠️ Ritardi
                    </button>
                    <button
                      onClick={refreshDailySummary}
                      className="text-xs bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors text-purple-700"
                    >
                      📊 Resoconto
                    </button>
                  </div>
                </div>
                
                {/* Feedback Buttons */}
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <p className="text-xs text-slate-400 mb-2">Segnalazioni</p>
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => startFeedback('bug')}
                      className="flex items-center gap-1 text-xs bg-red-50 border border-red-200 px-3 py-1.5 rounded-full hover:bg-red-100 transition-colors text-red-600"
                    >
                      <Bug className="w-3 h-3" />
                      Bug
                    </button>
                    <button
                      onClick={() => startFeedback('miglioramento')}
                      className="flex items-center gap-1 text-xs bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full hover:bg-emerald-100 transition-colors text-emerald-600"
                    >
                      <Lightbulb className="w-3 h-3" />
                      Idea
                    </button>
                  </div>
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-[#005293] to-[#0073CE] text-white rounded-br-sm'
                      : msg.tipo === 'daily_summary'
                        ? 'bg-gradient-to-br from-blue-50 to-purple-50 text-slate-700 rounded-bl-sm shadow-sm border border-blue-100'
                        : 'bg-white text-slate-700 rounded-bl-sm shadow-sm border border-slate-100'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white p-3 rounded-2xl rounded-bl-sm shadow-sm border border-slate-100">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#005293]" />
                    <span className="text-sm text-slate-400">Sto pensando...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-slate-100">
            {feedbackMode && (
              <div className={`mb-2 p-2 rounded-lg text-xs flex items-center gap-2 ${
                feedbackMode === 'bug' 
                  ? 'bg-red-50 text-red-600 border border-red-200' 
                  : 'bg-emerald-50 text-emerald-600 border border-emerald-200'
              }`}>
                {feedbackMode === 'bug' ? <Bug className="w-3 h-3" /> : <Lightbulb className="w-3 h-3" />}
                <span>Segnalazione {feedbackMode}</span>
                <button onClick={() => setFeedbackMode(null)} className="ml-auto hover:opacity-70">✕</button>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={feedbackMode ? `Descrivi...` : "Scrivi..."}
                className={`flex-1 rounded-full text-sm ${
                  feedbackMode 
                    ? feedbackMode === 'bug' 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-emerald-300 focus:border-emerald-500'
                    : 'border-slate-200 focus:border-[#005293]'
                }`}
                disabled={isLoading}
              />
              <Button
                onClick={() => sendMessage()}
                disabled={isLoading || !inputValue.trim()}
                className={`rounded-full w-10 h-10 p-0 ${
                  feedbackMode
                    ? feedbackMode === 'bug'
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-emerald-500 hover:bg-emerald-600'
                    : 'bg-gradient-to-r from-[#005293] to-[#0073CE] hover:from-[#004080] hover:to-[#0066BB]'
                }`}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Minimized Badge */}
      {isOpen && isMinimized && (
        <div 
          className="fixed bottom-44 right-6 bg-gradient-to-r from-[#005293] to-[#0073CE] text-white px-4 py-2 rounded-full shadow-lg cursor-pointer flex items-center gap-2 z-50"
          onClick={() => setIsMinimized(false)}
        >
          <img src="/logo.png" alt="PaMa" className="w-6 h-6 object-contain" />
          <span className="text-sm font-medium">PaMa Assistant</span>
        </div>
      )}

      {/* Floating Button */}
      <div
        className="fixed bottom-24 right-6 z-50 cursor-pointer"
        onClick={toggleChat}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div 
          className={`relative w-16 h-16 rounded-full bg-gradient-to-br from-[#005293] to-[#0073CE] shadow-xl flex items-center justify-center transition-all duration-300 ${
            isHovered ? 'scale-110 shadow-2xl' : ''
          }`}
          style={{ animation: !isOpen ? 'pulse 2s infinite' : 'none' }}
        >
          {!isOpen && (
            <>
              <div className="absolute inset-0 rounded-full bg-[#005293] animate-ping opacity-20" />
              <div className="absolute inset-0 rounded-full bg-[#005293] animate-pulse opacity-30" />
            </>
          )}
          
          <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            {isOpen ? (
              <X className="w-7 h-7 text-white" />
            ) : (
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center p-1 shadow-inner">
                <img 
                  src="/logo.png" 
                  alt="PaMa Assistant" 
                  className={`w-full h-full object-contain transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`}
                />
              </div>
            )}
          </div>
        </div>

        {/* Tooltip */}
        {!isOpen && isHovered && (
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-slate-800 text-white text-sm rounded-lg whitespace-nowrap">
            Chiedimi qualcosa!
            <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
          </div>
        )}

        {/* Notification badge - SOLO per notifiche di stato importanti */}
        {!isOpen && unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
            <Bell className="w-3 h-3" />
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(0, 82, 147, 0.4);
          }
          50% {
            box-shadow: 0 0 0 15px rgba(0, 82, 147, 0);
          }
        }
      `}</style>
    </>
  );
};

export default FloatingChatbot;
